import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../lib/auth';
import { api, ApiError } from '../lib/api';

interface LoginForm {
  email: string;
  password: string;
  organizationSlug: string;
}

interface LoginPageProps {
  onNavigate: (page: 'register' | 'forgot-password') => void;
}

export function LoginPage({ onNavigate }: LoginPageProps) {
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 2FA state
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [showRecoveryInput, setShowRecoveryInput] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    defaultValues: {
      organizationSlug: getOrgFromUrl(),
    },
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await api.post<{
        requiresTwoFactor?: boolean;
        userId?: string;
        accessToken?: string;
        refreshToken?: string;
        user?: unknown;
      }>('/auth/login', {
        email: data.email,
        password: data.password,
        organizationSlug: data.organizationSlug,
      });

      if (response.success && response.data?.requiresTwoFactor) {
        setTwoFactorRequired(true);
        setTwoFactorUserId(response.data.userId!);
        return;
      }

      // Normal login flow
      await login(data.email, data.password, data.organizationSlug);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const body: { userId: string; code?: string; recoveryCode?: string } = {
        userId: twoFactorUserId,
      };

      if (showRecoveryInput) {
        body.recoveryCode = twoFactorCode;
      } else {
        body.code = twoFactorCode;
      }

      const response = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: {
          id: string;
          email: string;
          role: string;
          organizationId: string;
          memberId?: string | null;
          organization: { id: string; name: string; slug: string };
          member?: { id: string; firstName: string; lastName: string } | null;
        };
      }>('/auth/2fa/login', body);

      if (response.success && response.data) {
        localStorage.setItem('openshul_access_token', response.data.accessToken);
        localStorage.setItem('openshul_refresh_token', response.data.refreshToken);
        api.setAccessToken(response.data.accessToken);
        window.location.reload();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (twoFactorRequired) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo-with-text.svg" alt="OpenShul" className="h-20 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h1>
            <p className="mt-2 text-gray-600">
              {showRecoveryInput
                ? 'Enter one of your recovery codes'
                : 'Enter the code from your authenticator app'}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <form onSubmit={handleTwoFactorSubmit} className="space-y-6">
              {error && (
                <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="twoFactorCode" className="block text-sm font-medium text-gray-700">
                  {showRecoveryInput ? 'Recovery Code' : 'Authentication Code'}
                </label>
                <input
                  id="twoFactorCode"
                  type="text"
                  autoComplete="one-time-code"
                  placeholder={showRecoveryInput ? 'Enter recovery code' : '000000'}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-center text-lg tracking-widest"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !twoFactorCode}
                className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setShowRecoveryInput(!showRecoveryInput);
                  setTwoFactorCode('');
                  setError(null);
                }}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                {showRecoveryInput ? 'Use authenticator code instead' : 'Use recovery code instead'}
              </button>
            </div>

            <div className="mt-2 text-center">
              <button
                onClick={() => {
                  setTwoFactorRequired(false);
                  setTwoFactorUserId('');
                  setTwoFactorCode('');
                  setShowRecoveryInput(false);
                  setError(null);
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Back to login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-with-text.svg" alt="OpenShul" className="h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Member Portal</h1>
          <p className="mt-2 text-gray-600">Sign in to access your account</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="organizationSlug" className="block text-sm font-medium text-gray-700">
                Organization
              </label>
              <input
                id="organizationSlug"
                type="text"
                placeholder="your-synagogue"
                {...register('organizationSlug', { required: 'Organization is required' })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {errors.organizationSlug && (
                <p className="mt-1 text-sm text-red-600">{errors.organizationSlug.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email', { required: 'Email is required' })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password', { required: 'Password is required' })}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => onNavigate('forgot-password')}
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Forgot your password?
            </button>
          </div>

          <div className="mt-4 text-center text-sm">
            <span className="text-gray-600">Don't have an account? </span>
            <button
              onClick={() => onNavigate('register')}
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Register
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function getOrgFromUrl(): string {
  // Extract org from subdomain (e.g., myshul.openshul.org) or query param
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const subdomain = parts[0];
  if (parts.length > 2 && subdomain && subdomain !== 'www' && subdomain !== 'staging' && subdomain !== 'portal') {
    return subdomain;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('org') ?? '';
}
